import { Box, Divider, styled, Tabs, tabsClasses } from '@mui/material';
import { useGetFlexWorker } from 'hq/hooks/queryHooks/flexWorkers/useGetFlexWorker';
import { LwTab, ModalPanelTabType } from 'redesign';
import { RenderUnsettledUI, SmallError, SmallLoader } from 'shared/components';
import { useState } from 'react';
import { useGetEnvironmentConfiguration } from 'shared/hooks/query-hooks/use-get-environment-configuration';
import { FlexWorkersFilters } from '@types';
import { FlexPools } from './tabs/flex-pools/flex-pools';
import { FlexWorkerDetailHead } from './flex-worker-detail-head';
import { FlexWorkerIdCheckDataProvider } from './tabs/id-check/flex-worker-id-check';
import { ShiftClaims } from './tabs/shift-claims/flex-worker-shift-claims';
import { FlexWorkerPersonalData } from './tabs/personal-data/flex-worker-personal-data';
import { FlexWorkerProfile } from './tabs/flex-worker-profile/profile/flex-worker-profile';
import { useFeatureFlagContext } from '../../../../../shared/contexts/feature-flag-provider';

type FlexWorkerDetailNewProps = {
  id: string;
  onClose: () => void;
  searchParams?: {
    level: FlexWorkersFilters['level'];
    employmentType: FlexWorkersFilters['employmentType'];
    disabled: boolean;
    filter?: string;
  };
};

export const FlexWorkerDetail = ({ id, onClose, searchParams }: FlexWorkerDetailNewProps) => {
  const flexWorkerQuery = useGetFlexWorker(id);
  const [selectedTab, setSelectedTab] = useState(0);
  const { allowEmploymentId, allowPSPOnboarding } = useFeatureFlagContext();
  const environmentConfiguration = useGetEnvironmentConfiguration(true);

  if (flexWorkerQuery.status !== 'success') {
    return (
      <RenderUnsettledUI
        renderLoading={<SmallLoader />}
        renderError={(error) => <SmallError error={error} />}
        data={flexWorkerQuery}
      />
    );
  }

  if (!environmentConfiguration.data) {
    return (
      <RenderUnsettledUI
        renderLoading={<SmallLoader />}
        renderError={(error) => <SmallError error={error} />}
        data={environmentConfiguration}
      />
    );
  }

  const tabs: ModalPanelTabType[] = [
    {
      id: 'Persoonlijke gegevens',
      tabLabel: 'Persoonlijke gegevens',
      element: <FlexWorkerPersonalData flexWorkerData={flexWorkerQuery.data} />,
    },
    {
      id: 'Profiel',
      tabLabel: 'Profiel',
      element: <FlexWorkerProfile flexWorkerId={id} />,
    },
    {
      id: 'Shiftclaims',
      tabLabel: 'Shiftclaims',
      element: <ShiftClaims flexWorkerId={id} />,
    },
    {
      id: 'Flexpools',
      tabLabel: 'Flexpools',
      element: (
        <FlexPools
          flexWorkerId={id}
          employmentType={flexWorkerQuery.data.employmentType}
          level={flexWorkerQuery.data.level}
        />
      ),
    },
    ...(flexWorkerQuery.data.hasPendingIdentityVerification
      ? [
          {
            id: 'ID check',
            tabLabel: 'ID check',
            element: (
              <FlexWorkerIdCheckDataProvider
                flexWorkerId={id}
                employmentType={flexWorkerQuery.data.employmentType}
                platformSettings={environmentConfiguration.data.backend.platformSettings}
                closeModal={onClose}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <FlexWorkerDetailWrapper>
      <FlexWorkerDetailHead
        data={flexWorkerQuery.data}
        onClose={onClose}
        allowEmploymentId={allowEmploymentId}
        allowPSPOnboarding={allowPSPOnboarding}
        searchParams={searchParams}
      />
      <Box mt={6} mb={6}>
        <Divider />
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        minHeight={0}
        flex="0 1 auto"
        paddingLeft="6px"
      >
        <StyledTabs
          value={tabs[selectedTab].id}
          onChange={(_, tabId) => {
            const selectedTab = tabs.findIndex((i) => i.id === tabId);
            setSelectedTab(selectedTab);
          }}
        >
          {tabs.map((tab) => {
            return <LwTab key={tab.id} value={tab.id} label={tab.tabLabel} />;
          })}
        </StyledTabs>
        <ContentWrapper role="tabpanel">{tabs[selectedTab].element}</ContentWrapper>
      </Box>
    </FlexWorkerDetailWrapper>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  [`.${tabsClasses.flexContainer}`]: {
    gap: theme.spacing(8),
  },
}));

const FlexWorkerDetailWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentWrapper = styled(Box)({
  height: '100%',
  position: 'relative',
  overflowY: 'auto',
  paddingLeft: '6px',
  '&::-webkit-scrollbar': {
    width: '22px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.1)',
    border: '8px solid white',
    borderRadius: '12px',
  },

  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    backgroundClip: 'content-box',
    border: '8px solid transparent',
    borderRadius: '12px',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.4)',
      backgroundClip: 'content-box',
      border: '8px solid transparent',
      borderRadius: '12px',
    },
  },
});
